import { Fire, Info } from "@phosphor-icons/react";

import FilterButton from "common/components/ui/FilterButton";
import Label from "common/components/ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from "common/components/ui/Select";
import Switch from "common/components/ui/Switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";
import { typedObjectEntries } from "common/helpers/utils";

import LimitsEditItem from "./LimitsEditItem";
import LimitsViewItem from "./LimitsViewItem";

import type {
  AccountLimits as AccountLimitsType,
  AccountLimitsWarmup,
} from "../../types";

const MAX_VALUE = 200;

const warmupSpeedValues = {
  LOW: "Slow",
  MEDIUM: "Medium",
  HIGH: "Fast",
} as const;

const sliders = [
  {
    label: "Connection requests",
    key: "connect",
  },
  {
    label: "Message requests",
    key: "message_request",
  },
  {
    label: "InMails",
    key: "inmail",
  },
] as const;

interface AccountLimitsProps {
  accountLimits: AccountLimitsType | undefined;
  setAccountLimits: (accountLimits: AccountLimitsType) => void;
  isLoading: boolean;
  isViewOnly: boolean;
}

export default function AccountLimits({
  accountLimits,
  setAccountLimits,
  isLoading,
  isViewOnly,
}: AccountLimitsProps) {
  function setValue(
    key: Exclude<keyof AccountLimitsType, "warmup">,
    value: number,
  ) {
    setAccountLimits({ ...accountLimits, [key]: value });
  }

  function setWarmupValue<Key extends keyof AccountLimitsWarmup>(
    key: Key,
    value: AccountLimitsWarmup[Key],
  ) {
    setAccountLimits({
      ...accountLimits,
      warmup: { ...accountLimits.warmup, [key]: value },
    });
  }

  const isWarmupEnabled = accountLimits?.warmup.enabled;

  return (
    <>
      <div className="flex flex-col gap-y-2">
        {sliders.map(({ key, label }) => {
          const value = accountLimits ? accountLimits[key] : 0;

          if (isViewOnly) {
            return (
              <LimitsViewItem
                key={label}
                label={label}
                value={value}
                maxValue={MAX_VALUE}
              />
            );
          }
          return (
            <LimitsEditItem
              key={label}
              label={label}
              value={value}
              setValue={(newValue) => setValue(key, newValue)}
              maxValue={MAX_VALUE}
              isLoading={isLoading}
              color="purple"
            />
          );
        })}
      </div>
      <div className="flex items-center px-2 py-2.5 gap-x-1.5">
        <Fire weight="fill" size={20} />
        <span className="text-button-sm">Warmup</span>
        <Switch
          size="sm"
          className="ml-auto"
          color="black"
          disabled={isLoading || isViewOnly}
          checked={isWarmupEnabled}
          onCheckedChange={(checked) => {
            setAccountLimits({
              ...accountLimits,
              warmup: checked
                ? {
                    enabled: true,
                    speed: "MEDIUM",
                    connect: 10,
                    inmail: 10,
                    message_request: 10,
                  }
                : { enabled: false },
            });
          }}
        />
      </div>
      {isWarmupEnabled && (
        <div className="flex flex-col gap-y-2 mb-3">
          {isViewOnly ? (
            <LimitsViewItem
              label="Warmup speed"
              value={warmupSpeedValues[accountLimits.warmup.speed]}
            />
          ) : (
            <div className="px-2 py-3 border border-black-200 rounded-3xl items-center flex">
              <Label size="sm" className="text-black-500 mr-0.5">
                Warmup speed
              </Label>
              <Tooltip>
                <TooltipTrigger>
                  <Info weight="fill" className="size-4 text-black-400" />
                </TooltipTrigger>
                <TooltipContent>
                  Warmup speed dictates how quickly an account should reach
                  their configured interaction limit. Fast - 7 days, Medium - 14
                  days, Slow - 30 days
                </TooltipContent>
              </Tooltip>
              <Select
                value={accountLimits.warmup.speed}
                onValueChange={(value) =>
                  setWarmupValue("speed", value as AccountLimitsWarmup["speed"])
                }
              >
                <SelectTrigger asChild className="ml-auto">
                  <FilterButton size="sm">
                    <SelectValue />
                  </FilterButton>
                </SelectTrigger>
                <SelectContent>
                  {typedObjectEntries(warmupSpeedValues).map(([key, label]) => {
                    return (
                      <SelectItem value={key} key={key}>
                        <SelectItemText>{label}</SelectItemText>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          {sliders.map(({ key, label }) => {
            const value = isWarmupEnabled ? accountLimits.warmup[key] : 0;

            if (isViewOnly) {
              return (
                <LimitsViewItem
                  key={label}
                  label={label}
                  value={value}
                  maxValue={accountLimits[key]}
                />
              );
            }
            return (
              <LimitsEditItem
                key={label}
                label={label}
                value={value}
                setValue={(newValue) => setWarmupValue(key, newValue)}
                maxValue={accountLimits[key]}
                isLoading={isLoading}
                color="orange"
              />
            );
          })}
        </div>
      )}
    </>
  );
}
